import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { getOrderDisplayReference } from "@/lib/order-display-reference";
import { convertDate } from "@/lib/utils";
import type { StoreOrder } from "@medusajs/types";
import OrderFulfillmentDetails from "./order-fulfillment-details";

type OrderDetailsCardProps = {
  locale: Locales;
  order: StoreOrder;
};

export type CountryCode = "be" | "fr";

const countries: Record<CountryCode, string> = {
  be: "Belgium",
  fr: "France",
};

type AddressMetadata = {
  contact_person?: string;
  vat_number?: string;
  country_code?: string;
};

const getCountryLabel = (locale: Locales, code?: string | null) => {
  if (code === "be" || code === "fr") {
    return __(countries[code], locale);
  }

  return code ? code.toUpperCase() : "-";
};

const OrderDetailsCard = ({ locale, order }: OrderDetailsCardProps) => {
  const firstName = order?.metadata?.first_name as string | undefined;
  const lastName = order?.metadata?.last_name as string | undefined;
  const email = order?.metadata?.email as string | undefined;
  const phone = order?.metadata?.phone as string | undefined;
  const billingMetadata = (order?.billing_address?.metadata || {}) as AddressMetadata;
  const billingCompany = order?.billing_address?.company;
  const billingFields = [
    billingCompany,
    billingMetadata.contact_person,
    billingMetadata.vat_number,
    order.billing_address?.address_1,
    order.billing_address?.postal_code,
    order.billing_address?.city,
  ];
  const hasBillingAddress = billingFields.some(Boolean);

  const basketId = order.metadata?.varyBasketId as number | string | null | undefined;
  const fileId = order.metadata?.varyFileId as number | string | null | undefined;
  const displayId = getOrderDisplayReference({
    orderId: order.id,
    varyBasketId: basketId,
    varyFileId: fileId,
  });

  return (
    <section className="space-y-5">
      <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">{__("Order Details", locale)}</h2>
      <div className="border-primary-light/50 rounded-md border p-4">
        <dl className="grid gap-2.5 md:grid-cols-2 md:gap-20">
          <div className="flex flex-wrap items-center justify-between gap-1.5">
            <dt>{__(fileId != null ? "Vary file number" : "Order ID", locale)}:</dt>
            <dd className="font-semibold break-all">{displayId}</dd>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-1.5 capitalize">
            <dt>{__("Order Date", locale)}:</dt>
            <dd className="font-semibold">
              {order?.created_at ? convertDate(order?.created_at, "dd MMM yyyy", locale) : "-"}
            </dd>
          </div>
        </dl>
      </div>
      <OrderFulfillmentDetails locale={locale} metadata={order.metadata} />
      <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">{__("Delivery Address", locale)}</h2>
      <div className="border-primary-light/30 space-y-2.5 rounded-md border p-4">
        <dl className="grid gap-10 md:grid-cols-2 md:gap-7.5">
          <div className="w-fit space-y-2.5">
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("Name", locale)} :</dt>
              <dd className="font-semibold">{firstName + " " + lastName}</dd>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("Email", locale)} : </dt>
              <dd className="font-semibold">{email}</dd>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("Phone", locale)} :</dt>
              <dd className="font-semibold">{phone}</dd>
            </div>
          </div>
          <div className="w-fit space-y-2.5">
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("Address", locale)} :</dt>
              <dd className="font-semibold">{order.shipping_address?.address_1}</dd>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("City", locale)} :</dt>
              <dd className="font-semibold">{order.shipping_address?.city}</dd>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <dt>{__("Country", locale)} : </dt>
              <dd className="font-semibold">{getCountryLabel(locale, order?.shipping_address?.country_code)}</dd>
            </div>
          </div>
        </dl>
      </div>
      {hasBillingAddress && (
        <>
          <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">{__("Billing Address", locale)}</h2>
          <div className="border-primary-light/30 space-y-2.5 rounded-md border p-4">
            <dl className="grid gap-10 md:grid-cols-2 md:gap-7.5">
              <div className="w-fit space-y-2.5">
                {billingCompany && (
                  <div className="flex flex-wrap gap-1.5">
                    <dt>{__("Company", locale)} :</dt>
                    <dd className="font-semibold">{billingCompany}</dd>
                  </div>
                )}
                {billingMetadata.contact_person && (
                  <div className="flex flex-wrap gap-1.5">
                    <dt>{__("Contact person", locale)} :</dt>
                    <dd className="font-semibold">{billingMetadata.contact_person}</dd>
                  </div>
                )}
                {billingMetadata.vat_number && (
                  <div className="flex flex-wrap gap-1.5">
                    <dt>{__("VAT Number", locale)} :</dt>
                    <dd className="font-semibold">{billingMetadata.vat_number}</dd>
                  </div>
                )}
              </div>
              <div className="w-fit space-y-2.5">
                {order.billing_address?.address_1 && (
                  <div className="flex flex-wrap gap-1.5">
                    <dt>{__("Address", locale)} :</dt>
                    <dd className="font-semibold">{order.billing_address.address_1}</dd>
                  </div>
                )}
                {order.billing_address?.postal_code && (
                  <div className="flex flex-wrap gap-1.5">
                    <dt>{__("ZIP Code", locale)} :</dt>
                    <dd className="font-semibold">{order.billing_address.postal_code}</dd>
                  </div>
                )}
                {order.billing_address?.city && (
                  <div className="flex flex-wrap gap-1.5">
                    <dt>{__("City", locale)} :</dt>
                    <dd className="font-semibold">{order.billing_address.city}</dd>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  <dt>{__("Country", locale)} : </dt>
                  <dd className="font-semibold">
                    {getCountryLabel(locale, order.billing_address?.country_code || billingMetadata.country_code)}
                  </dd>
                </div>
              </div>
            </dl>
          </div>
        </>
      )}
    </section>
  );
};

export default OrderDetailsCard;
